import React from 'react';
import { Box, Chip, Paper, Stack, Typography, useTheme } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { formatBookingCurrency } from '../../utils/bookingPaymentSummary';

export default function BookingOverpaidBanner({
  returnAmount = 0,
  paidAmount = 0,
  totalAmount = 0,
  currency = 'INR',
  isDark: isDarkProp,
  detailLabel = 'booking total',
  compact = true,
}) {
  const theme = useTheme();
  const isDark = isDarkProp ?? theme.palette.mode === 'dark';
  const amount = Number(returnAmount || 0);

  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  const accent = isDark ? '#ffb74d' : '#e65100';
  const accentSoft = isDark ? 'rgba(255, 152, 0, 0.18)' : 'rgba(255, 152, 0, 0.14)';
  const detailText = `Received ${formatBookingCurrency(paidAmount, currency)} against ${detailLabel} ${formatBookingCurrency(totalAmount, currency)}.`;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        px: compact ? 1.25 : 1.5,
        py: compact ? 0.9 : 1.25,
        borderRadius: '12px',
        background: isDark
          ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 87, 34, 0.1) 100%)'
          : 'linear-gradient(135deg, #fff8e1 0%, #ffe0b2 100%)',
        border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.42)' : 'rgba(255, 152, 0, 0.35)'}`,
        boxShadow: isDark
          ? '0 6px 18px rgba(255, 152, 0, 0.12)'
          : '0 6px 16px rgba(255, 152, 0, 0.16)',
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box
          sx={{
            width: compact ? 34 : 40,
            height: compact ? 34 : 40,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: accentSoft,
            border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.35)' : 'rgba(255, 152, 0, 0.28)'}`,
          }}
        >
          <ReplayIcon sx={{ color: '#ff9800', fontSize: compact ? 18 : 22 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              label="Overpaid"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                backgroundColor: isDark ? 'rgba(255, 152, 0, 0.22)' : '#fff3e0',
                color: accent,
                border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.4)' : 'rgba(255, 152, 0, 0.35)'}`,
              }}
            />
            <Typography
              variant={compact ? 'subtitle1' : 'h6'}
              sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                color: isDark ? '#fff' : '#333',
              }}
            >
              {formatBookingCurrency(amount, currency)}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              color: 'text.secondary',
              fontSize: compact ? '0.72rem' : '0.75rem',
              lineHeight: 1.35,
            }}
          >
            {detailText}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
